import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
  IconButton,
  //Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { RecoveryService } from '../../logic/domain/services/recoveryService';
import { RecoveryImplementation } from '../../logic/infraestructure/recovery/recoveryImplementation';
import { ContactOption } from '../../logic/domain/interfaces/recoveryApi';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import logo from '../assets/logo.png';

const steps = ['Buscar Paciente', 'Seleccionar Contacto', 'Verificar Código', 'Nueva Contraseña'];

const RecoverPassword: React.FC = () => {
  const navigate = useNavigate();
  const recoveryService = new RecoveryService(new RecoveryImplementation());

  const [activeStep, setActiveStep] = useState(0);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Step 1: Buscar paciente
  const [tipoDoc, setTipoDoc] = useState('DNI');
  const [numeroDoc, setNumeroDoc] = useState('');
  const [token, setToken] = useState('');

  // Step 2: Opciones de contacto
  const [contactOptions, setContactOptions] = useState<ContactOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<number>(0);

  // Step 3: Código de verificación
  const [code, setCode] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(600); // 10 minutos en segundos
  const [timerActive, setTimerActive] = useState(false);

  // Step 4: Nueva contraseña
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleFindPatient = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!captchaValid || !captchaValue) {
      setError("Por favor, valida el reCAPTCHA antes de continuar.");
      setLoading(false);
      return;
    }

    try {
      const newToken = await recoveryService.findPatient(tipoDoc, numeroDoc);
      setToken(newToken);

      // Obtener opciones de contacto
      const options = await recoveryService.getContactOptions(newToken);

      // Validar que options sea un array
      if (!Array.isArray(options)) {
        console.error('contactOptions no es un array:', options);
        throw new Error("Error: formato de respuesta inválido");
      }

      setContactOptions(options);
      setActiveStep(1);
      setCaptchaValid(false);
      setCaptchaValue(null);
    } catch (err) {
      //const errorMessage = (err instanceof Error ? err.message : "Error al buscar paciente");
      const errorMessage = "Error al buscar paciente";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError("");

    try {
      const newToken = await recoveryService.sendCode(token, selectedOption);
      setToken(newToken);
      setSuccess("Código enviado correctamente");
      setTimeRemaining(600); // Reiniciar el contador a 10 minutos
      setTimerActive(true); // Activar el temporizador
      setActiveStep(2);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error al enviar código";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const newToken = await recoveryService.validateCode(token, code);
      setToken(newToken);
      setSuccess("Código validado correctamente");
      setActiveStep(3);
      setTimerActive(false); // Detener el temporizador
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Código inválido";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      setLoading(false);
      return;
    }

    try {
      await recoveryService.changePassword(token, newPassword);
      setSuccess("Contraseña cambiada exitosamente. Redirigiendo al login...");
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error al cambiar contraseña";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Efecto para el temporizador
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [timerActive, timeRemaining]);

  // Función para formatear el tiempo
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <AppBar position="static" sx={{ background: "#00A3A8", boxShadow: "none" }}>
        <Toolbar sx={{ minHeight: "50px", justifyContent: "center" }}>
          <img src={logo} alt="Logo" style={{ height: 35 }} />
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 4,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            width: "90%",
            maxWidth: 600,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography component="h1" variant="h4" sx={{ mb: 3, textAlign: "center", fontWeight: 600 }}>
            Recuperar contraseña
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4, '& .MuiStepIcon-text': { fill: 'white' } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {/* Step 1: Buscar Paciente */}
          {activeStep === 0 && (
            <Box component="form" onSubmit={handleFindPatient}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="tipoDoc-label">Tipo de Documento</InputLabel>
                <Select
                  labelId="tipoDoc-label"
                  value={tipoDoc}
                  label="Tipo de Documento"
                  onChange={(e) => setTipoDoc(e.target.value)}
                  required
                >
                  <MenuItem value="DNI">DNI</MenuItem>
                  <MenuItem value="CUIL">CUIL</MenuItem>
                  <MenuItem value="CI">CI</MenuItem>
                  <MenuItem value="LC">LC</MenuItem>
                  <MenuItem value="PAS">PAS</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="N° Documento"
                variant="outlined"
                fullWidth
                margin="normal"
                value={numeroDoc}
                onChange={(e) => setNumeroDoc(e.target.value)}
                required
              />

              <Box sx={{ mb: 0, mt: 2, display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  sitekey="6LdKTBIkAAAAAB3kKeR0llkoEqMcJPOzklJRBtcD"
                  onChange={(value) => {
                    setCaptchaValue(value);
                    setCaptchaValid(!!value);
                    setError("");
                  }}
                  onExpired={() => {
                    setCaptchaValid(false);
                    setCaptchaValue(null);
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !captchaValid}
                sx={{ mt: 3, py: 1.5, bgcolor: "#00A3A8", "&:hover": { bgcolor: "#008B8F" }, color: "white" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Buscar'}
              </Button>

              <Button
                variant="text"
                fullWidth
                onClick={() => navigate('/login')}
                sx={{ mt: 2, color: "#00A3A8" }}
              >
                Volver al inicio de sesión
              </Button>
            </Box>
          )}

          {/* Step 2: Seleccionar Contacto */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Seleccione cómo desea recibir el código de verificación:
              </Typography>

              <RadioGroup value={selectedOption} onChange={(e) => setSelectedOption(Number(e.target.value))}>
                {Array.isArray(contactOptions) && contactOptions.length > 0 ? (
                  contactOptions.map((option) => (
                    <FormControlLabel
                      key={option.id}
                      value={option.id}
                      control={<Radio sx={{ color: "#00A3A8", "&.Mui-checked": { color: "#00A3A8" } }} />}
                      label={option.tipo === 'cad' ? option.descripcion : `${option.tipo}: ${option.descripcion}`}
                    />
                  ))
                ) : (
                  <Typography color="error">No hay opciones de contacto disponibles</Typography>
                )}
              </RadioGroup>

              <Button
                variant="contained"
                fullWidth
                disabled={loading}
                onClick={() => {
                  const selected = contactOptions.find((o) => o.id === selectedOption);
                  if (selected?.tipo === 'cad') {
                    window.open(
                      'https://api.whatsapp.com/send?phone=5492995809502&text=%C2%A1Hola%21+Estoy+en+la+sito+web+y+quiero+pedir+m%C3%A1s+informaci%C3%B3n',
                      '_blank'
                    );
                  } else {
                    handleSendCode();
                  }
                }}
                sx={{ mt: 3, py: 1.5, bgcolor: "#00A3A8", "&:hover": { bgcolor: "#008B8F" }, color: "white" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar Código'}
              </Button>
              <Button
                variant="text"
                fullWidth
                onClick={() => { setActiveStep(0); setError(""); setSuccess(""); }}
                sx={{ mt: 1, color: "#00A3A8" }}
              >
                Volver
              </Button>
            </Box>
          )}

          {/* Step 3: Verificar Código */}
          {activeStep === 2 && (
            <Box component="form" onSubmit={handleValidateCode}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Ingrese el código de 6 dígitos que recibió:
              </Typography>

              <TextField
                label="Código de Verificación"
                variant="outlined"
                fullWidth
                margin="normal"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                inputProps={{ maxLength: 6 }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Tiempo restante para ingresar el código: {formatTime(timeRemaining)}
              </Typography>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading && timerActive}
                sx={{ mt: 3, py: 1.5, bgcolor: "#00A3A8", "&:hover": { bgcolor: "#008B8F" }, color: "white" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Validar Código'}
              </Button>
              
            <Button
              variant="text"
              fullWidth
              disabled={loading || !captchaValid}
              onClick={() => { setActiveStep(1); setCode(""); setError(""); setSuccess(""); }}
              sx={{ mt: 1, color: "#00A3A8" }}
            >
              Volver
            </Button>

            </Box>
          )}

          {/* Step 4: Nueva Contraseña */}
          {activeStep === 3 && (
            <Box component="form" onSubmit={handleChangePassword}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Ingrese su nueva contraseña:
              </Typography>

              <TextField
                label="Nueva Contraseña"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Confirmar Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ mt: 3, py: 1.5, bgcolor: "#00A3A8", "&:hover": { bgcolor: "#008B8F" }, color: "white" }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Cambiar Contraseña'}
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Clínica Dr. Roberto Raña
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default RecoverPassword;
